import { ArrowUpDown, CircleCheck, ExternalLinkIcon } from 'lucide-react'

import { SplitsLink } from '~/components/Link'
import { Tooltip } from '~/components/component-library'
import { LoadingSpinner } from '~/components/component-library/LoadingSpinner'
import { Timestamp } from '~/components/component-library/Timestamp'
import { UserDisplay } from '~/components/component-library/UserDisplay'
import { UserPopover } from '~/components/component-library/UserDisplay/components/UserPopover'
import { TransactionApprovers } from '~/components/txn-display/timeline/components/TransactionApprovers'
import { useCancelledBy } from '~/components/txn-display/timeline/hooks/useCancelledBy'
import { useRemainingSignatures } from '~/components/txn-display/timeline/hooks/useRemainingSignatures'
import { useActiveOrgId } from '~/hooks/org/useActiveOrgId'
import { useAuthUser } from '~/hooks/useAuthUser'
import { useSmartAccount } from '~/hooks/useSmartAccounts'
import { CheckCircle } from '~/icons/CheckCircle'
import { OctagonAlert } from '~/icons/OctagonAlert'
import { TriangleAlert } from '~/icons/TriangleAlert'
import {
  BackendTransaction,
  BasicTransactionType,
  TransactionStatus,
} from '~/types'
import { getExplorerTransactionLinkChainId } from '~/utils/addresses'

import type { TransactionTimelineRowProps } from '~/components/txn-display/timeline/components/TimelineRow'

export function useStatusRow({
  transaction,
}: {
  transaction: BackendTransaction
}): TransactionTimelineRowProps | null {
  const { smartAccount } = useSmartAccount(transaction.smartAccountAddress)
  const { remaining } = useRemainingSignatures({ transaction })
  const cancelledBy = useCancelledBy(transaction)
  const activeOrgId = useActiveOrgId()
  const { user: authUser } = useAuthUser()
  const isCurrentUserCancelled =
    !!cancelledBy && !!authUser && authUser.id === cancelledBy.id

  if (!smartAccount) return null

  const successTimestamp =
    ('transactionTime' in transaction
      ? transaction.transactionTime
      : undefined) ??
    transaction.submittedAt ??
    transaction.updatedAt
  const failureTimestamp = transaction.updatedAt ?? transaction.submittedAt

  switch (transaction.status) {
    case TransactionStatus.Succeeded: {
      let content: React.ReactNode = 'Completed'

      let icon = <CheckCircle className="size-4 text-green-500" />

      if (!transaction.isMerkleTransaction) {
        const isBridgeTransaction = transaction.types?.includes(
          BasicTransactionType.Bridge,
        )

        if (isBridgeTransaction) {
          content = (
            <SplitsLink
              path={getExplorerTransactionLinkChainId(
                transaction.transactionHash,
                transaction.chainId,
              )}
              target="tab"
              className="hover:underline"
            >
              <div className="flex items-center gap-1">
                <p>Submitted</p>
                <ExternalLinkIcon className="size-3" />
              </div>
            </SplitsLink>
          )
          icon = <ArrowUpDown className="size-4 text-secondary" />
        }
      }

      return {
        key: `status-${transaction.id}-succeeded`,
        icon,
        content,
        meta: successTimestamp ? (
          <Timestamp
            timestamp={successTimestamp}
            type="relative"
            abbreviate={true}
          />
        ) : (
          ''
        ),
      }
    }
    case TransactionStatus.Failed: {
      const revertReason =
        'revertReason' in transaction
          ? transaction.revertReason
          : 'revertReasonRaw' in transaction
            ? transaction.revertReasonRaw
            : undefined

      return {
        key: `status-${transaction.id}-failed`,
        icon: <OctagonAlert className="size-4 fill-red-500" />,
        content: `Failed ${revertReason ? `- ${revertReason}` : ''}`,
        meta: failureTimestamp ? (
          <Timestamp
            timestamp={failureTimestamp}
            type="relative"
            abbreviate={true}
          />
        ) : (
          ''
        ),
      }
    }
    case TransactionStatus.Cancelled: {
      return {
        key: `status-${transaction.id}-cancelled`,
        icon: <TriangleAlert className="size-4 fill-orange-500" />,
        content: cancelledBy ? (
          <UserPopover user={cancelledBy} align="start">
            <span className="flex min-w-0 items-center gap-1">
              <span className="flex-shrink-0 whitespace-nowrap">
                Cancelled by
              </span>
              <span className="min-w-0 truncate font-medium text-primary">
                {isCurrentUserCancelled ? (
                  'you'
                ) : (
                  <UserDisplay
                    user={cancelledBy}
                    layout="inline-text"
                    hidePopover
                  />
                )}
              </span>
            </span>
          </UserPopover>
        ) : (
          <span>Cancelled</span>
        ),
        meta: failureTimestamp ? (
          <Timestamp
            timestamp={failureTimestamp}
            type="relative"
            abbreviate={true}
          />
        ) : (
          ''
        ),
      }
    }
    case TransactionStatus.Expired: {
      return {
        key: `status-${transaction.id}-expired`,
        icon: <TriangleAlert className="size-4 fill-orange-500" />,
        content: 'Expired',
        meta: failureTimestamp ? (
          <Timestamp
            timestamp={failureTimestamp}
            type="relative"
            abbreviate={true}
          />
        ) : (
          ''
        ),
      }
    }
    case TransactionStatus.Submitted: {
      return {
        key: `status-${transaction.id}-submitted`,
        icon: <LoadingSpinner size="lg" thickness="thin" variant="default" />,
        content: 'Confirming',
        meta: '',
      }
    }
    case TransactionStatus.Approved: {
      return {
        key: `status-${transaction.id}-approved`,
        icon: <CircleCheck className="size-4 text-secondary" />,
        content: 'Approved',
        meta: '',
      }
    }
    default: {
      if (!remaining) {
        return {
          key: `status-${transaction.id}-awaiting-submission`,
          icon: <LoadingSpinner size="lg" thickness="thin" variant="default" />,
          content: 'Awaiting submission',
          meta: '',
        }
      }

      // check if this is your org
      const isYourOrg = smartAccount.orgId === activeOrgId
      const text = `${remaining} approval${remaining === 1 ? '' : 's'} needed`

      return {
        key: `status-${transaction.id}-approvals-needed`,
        icon: (
          <div className="relative grid aspect-square h-4 grid-cols-2 overflow-hidden rounded-full p-[3px] ring-[1.5px] ring-inset ring-gray-600 dark:ring-gray-400">
            <div className="h-full w-full rounded-l-full bg-gray-600 dark:bg-gray-400" />
          </div>
        ),
        content: isYourOrg ? (
          <Tooltip
            align="start"
            content={
              <TransactionApprovers
                transaction={transaction}
                smartAccount={smartAccount}
              />
            }
          >
            <span>{text}</span>
          </Tooltip>
        ) : (
          <span>{text}</span>
        ),
        meta: '',
      }
    }
  }
}
